import React, { useRef, useState } from "react";
import { Box, CssBaseline, Drawer, IconButton, useMediaQuery } from "@mui/material";
import Sidebar from "@/components/layout/Sidebar";
import Topbar from "@/components/layout/Topbar";
import "dayjs/locale/ko";
import { SIDEBAR_WIDTH, TOPBAR_HEIGHT } from "@/types/common";
import { MainLayoutRefContext } from "@/contexts/MainLayoutRefContext";
import MenuIcon from "@mui/icons-material/Menu";

interface MainLayoutProps {
  children: React.ReactNode;
}

const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
  const isMobile = useMediaQuery("(max-width:1280px)");
  const [sidebarOpen, setSidebarOpen] = React.useState(!isMobile);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const mainRef = useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    setSidebarOpen(!isMobile);
  }, [isMobile]);

  const handleSidebarToggle = () => {
    setSidebarOpen(!sidebarOpen);
  };

  return (
    <Box
      sx={{
        display: "flex",
        minHeight: "100vh",
        width: "100%",
        maxWidth: { xs: "100%", lg: "1920px" },
        mx: "auto",
      }}
    >
      <CssBaseline />
      {isMobile && !drawerOpen && (
        <Box sx={{ position: "fixed", top: 16, left: 16, zIndex: 1301 }}>
          <IconButton onClick={() => setDrawerOpen(true)}>
            <MenuIcon />
          </IconButton>
        </Box>
      )}
      <Drawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        variant='temporary'
        transitionDuration={{ enter: 500, exit: 400 }}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: "block", lg: "none" },
          "& .MuiDrawer-paper": { width: SIDEBAR_WIDTH },
        }}
      >
        <Sidebar open={drawerOpen} onClose={() => setDrawerOpen(false)} isMobile={true} width={SIDEBAR_WIDTH} />
      </Drawer>
      <Topbar
        onMenuClick={handleSidebarToggle}
        isMobile={isMobile}
        sidebarOpen={sidebarOpen}
        sidebarWidth={SIDEBAR_WIDTH}
      />
      <Sidebar open={sidebarOpen} onClose={handleSidebarToggle} isMobile={isMobile} width={SIDEBAR_WIDTH} />
      <MainLayoutRefContext.Provider value={{ mainRef, sidebarOpen }}>
        <Box
          ref={mainRef}
          component='main'
          sx={{
            flexGrow: 1,
            p: 0,
            width: "100%",
            mt: `${TOPBAR_HEIGHT}px`,
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "column",
            height: `calc(100vh - ${TOPBAR_HEIGHT}px)`,
            minWidth: `calc(1280px - ${isMobile ? 0 : SIDEBAR_WIDTH}px)`,
          }}
        >
          {children}
        </Box>
      </MainLayoutRefContext.Provider>
    </Box>
  );
};

export default MainLayout;
